import * as rd from '@devexperts/remote-data-ts'
import {
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from '@reduxjs/toolkit'
import { MapInitParam } from '@spiaggeit/map-canvas'
import { Color, DecorationType, SpotType } from '@spiaggeit/spit-core'
import { t } from 'i18next'

import { Service } from '@/models/service'

import { resetAllState } from '@/store/commonActions.ts'
import { Errors } from '@/utils/errors'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { normalizeSpot } from '@/utils/normalizeSpot'
import { httpClient } from '../lib/http/HttpClient'
import {
  MapElementWithSectorId,
  BoundingBox2d,
  MapResponse,
  MapSetupItem,
  MapSetupItemKeyName,
  Sector,
  SectorDetailType,
} from '../models/map'
import {
  MAP_CELL_SIZE,
  mapSetupElements,
  spotTypesWithoutSetupSeats,
} from '../utils/constants'
import { fromISOtoTimestamp } from '../utils/dateUtils'
import { getFormattedMapElement } from '../utils/getFormattedMapElement'
import { getHalfDayValueFromBookingPeriod } from '../utils/halfDay'

import { appSlice } from './appSlice'
import { RootState } from './configureStore'
import { createAppSlice } from './createAppSlice'
import { bookingPeriodSelector, insertPeriodSlice } from './insertPeriodSlice'
import { licenseSlice } from './licenseSlice'
import { sectorRulesSlice } from './sectorRulesSlice'
import { servicesSlice } from './servicesSlice'

type MapInitOpts = MapInitParam['opts']

interface ExtendedMapInitOpts extends MapInitOpts {
  elements: MapElementWithSectorId[]
  bestQuadrant: BoundingBox2d | null
}

export type MapSliceState = {
  mapData: rd.RemoteData<string, Partial<ExtendedMapInitOpts>>
  selectedElementId?: string
  selectedElement: MapElementWithSectorId | null
  currentSector: Sector | null
  currentSectorDetails: Sector['details'] | null
  sectors: Sector[]
  selectedElementSetupData: {
    selectedElementSetupItems: MapSetupItem[]
    selectedElementSetupLimit: MapSetupItem | null
  }
}

const initialState: MapSliceState = {
  currentSector: null,
  currentSectorDetails: null,
  mapData: rd.initial,
  sectors: [],
  selectedElement: null,
  selectedElementSetupData: {
    selectedElementSetupItems: [],
    selectedElementSetupLimit: null,
  },
}

export const loadMapData = createAsyncThunk<
  Partial<MapInitParam['opts']>,
  { screenWidth: number; screenHeight: number; cellSize: number },
  { rejectValue: string; state: RootState }
>(
  'map/loadMapData',
  async ({ screenWidth, screenHeight, cellSize }, thunkApi) => {
    const state = thunkApi.getState()
    const period = insertPeriodSlice.selectors.period(state)
    const bookingPeriod = bookingPeriodSelector(state)
    const country = appSlice.selectors.country(state)
    const sct = appSlice.selectors.sct(state)

    if (!period || !period.end || !period.start) {
      throw thunkApi.rejectWithValue(Errors.PeriodNotFound)
    }

    const from = fromISOtoTimestamp(period.start)
    const to = fromISOtoTimestamp(period.end)
    const halfDay = getHalfDayValueFromBookingPeriod(bookingPeriod)
    const license = licenseSlice.selectors.licenseCode(state)
    const preemptionCode = sectorRulesSlice.selectors.preemptionCode(state)
    const unavailableSectors =
      sectorRulesSlice.selectors.unavailableSectors(state)

    try {
      const searchParams = new URLSearchParams()
      if (typeof from === 'number') {
        searchParams.set('from', String(from))
      }
      if (typeof to === 'number') {
        searchParams.set('to', String(to))
      }
      if (Array.isArray(sct) && sct.length > 0) {
        searchParams.set('sct', sct.join(','))
      }
      searchParams.set('halfDay', String(halfDay))
      searchParams.set('v2Payload', '1')
      searchParams.set('screenWidth', String(screenWidth))
      searchParams.set('screenHeight', String(screenHeight))
      searchParams.set('cellWidth', String(cellSize))
      searchParams.set('cellHeight', String(cellSize))
      if (preemptionCode) {
        searchParams.set('preemptionCode', preemptionCode)
      }

      const response = await httpClient.fetch<MapResponse>(
        `beaches/${license}/map-v2?${searchParams.toString()}`,
        country
      )

      if (response.status === 'success' && response.data) {
        const data = response.data.result

        return {
          background: data.background
            ? {
                height: data.background.height * MAP_CELL_SIZE,
                offset: {
                  x: data.background.posX,
                  y: data.background.posY,
                },
                url: data.background.url,
                width: data.background.width * MAP_CELL_SIZE,
              }
            : {
                height: 0,
                offset: {
                  x: 0,
                  y: 0,
                },
                url: null,
                width: 0,
              },
          bestQuadrant: data.bestQuadrant,
          elements: data.elements.map((element) =>
            getFormattedMapElement(
              element,
              rd.isSuccess(unavailableSectors) ? unavailableSectors.value : []
            )
          ),
          size: {
            height: data.height,
            width: data.width,
          },
        }
      }

      throw new Error('Unexpected response structure')
    } catch (_error) {
      return thunkApi.rejectWithValue('Failed to fetch map data')
    }
  }
)

export const loadSectors = createAsyncThunk<
  Sector[],
  void,
  { rejectValue: string; state: RootState }
>('map/loadSectors', async (_, thunkApi) => {
  const state = thunkApi.getState()
  const country = appSlice.selectors.country(state)
  const sct = appSlice.selectors.sct(state)
  const license = licenseSlice.selectors.licenseCode(state)

  try {
    const response = await httpClient.fetch<{ result: { sectors: Sector[] } }>(
      `beaches/${license}/sectors`,
      country
    )

    if (response.status === 'success' && response.data) {
      const { sectors } = response.data.result

      if (Array.isArray(sct) && sct.length > 0) {
        return sectors.filter((sector) => sct.includes(sector.header.oldId))
      }

      return sectors
    }

    throw new Error('Unexpected response structure')
  } catch (_error) {
    return thunkApi.rejectWithValue('Failed to fetch sectors data')
  }
})

export const mapSlice = createAppSlice({
  extraReducers: (builder) => {
    builder.addCase(resetAllState, () => initialState)
    builder
      .addCase(loadMapData.pending, (state) => {
        state.mapData = rd.pending
      })
      .addCase(
        loadMapData.fulfilled,
        (state, action: PayloadAction<Partial<MapInitParam['opts']>>) => {
          state.mapData = rd.success(action.payload)
        }
      )
      .addCase(loadMapData.rejected, (state, action) => {
        state.mapData = rd.failure(action.payload as string)
      })
      .addCase(loadSectors.pending, (state) => {
        state.sectors = []
      })
      .addCase(
        loadSectors.fulfilled,
        (state, action: PayloadAction<Sector[]>) => {
          state.sectors = action.payload
        }
      )
      .addCase(loadSectors.rejected, (state) => {
        state.sectors = []
      })
  },
  initialState,
  name: 'map',
  reducers: (create) => ({
    selectMapElement: create.reducer(
      (state, action: PayloadAction<MapElementWithSectorId | null>) => {
        state.selectedElementId = action.payload?.id
      }
    ),
  }),
})

export const mapSelectors = {
  error: (state: RootState): string | null =>
    rd.fold<string, Partial<MapInitParam['opts']>, string | null>(
      () => null,
      () => null,
      (e) => e,
      () => null
    )(state.map.mapData),
  hasError: (state: RootState) => rd.isFailure(state.map.mapData),
  isLoading: (state: RootState) => rd.isPending(state.map.mapData),
  mapData: (state: RootState) => {
    if (rd.isFailure(state.map.mapData)) {
      return null
    } else if (rd.isSuccess(state.map.mapData)) {
      return state.map.mapData.value
    }
  },
  mapRemoteData: (state: RootState) => state.map.mapData,
  mappedSectors: (state: RootState) => {
    if (rd.isFailure(state.map.mapData)) {
      return []
    } else if (rd.isSuccess(state.map.mapData)) {
      const mapData = state.map.mapData.value
      return state.map.sectors.map((sector) => {
        // This filters all unavailable map element + sorts them by ids and category
        // (e.g.: Sector 1 may have 5 umbrellas and 2 boats, which we need to store in two separate arrays)
        const filteredElements = mapData?.elements
          ?.filter((element) => {
            const isAvailable = element.style?.iconColor === Color.GREEN
            const isDecoration = Object.values(DecorationType).includes(
              element?.subType as DecorationType
            )
            const isValidSpot = isAvailable && !isDecoration
            return element.sectorId === sector.header.id && isValidSpot
          })
          // Deduplicate based on sectorId, name, and subType, this is useful when
          //  we have multiple map elements for the same "Sunbed area" spot since
          // we only need to show the first one when using sector booking mode
          ?.filter((element, index, self) => {
            return (
              self.findIndex((e) =>
                getObjectsComparison(normalizeSpot(e), normalizeSpot(element))
              ) === index
            )
          })

        const sectorElementsGroupedByType = Object.values(
          filteredElements?.reduce<Record<string, MapElementWithSectorId[]>>(
            (acc, item) => {
              const key = item.subType as SpotType
              if (!acc[key]) {
                acc[key] = []
              }
              acc[key].push(item)
              return acc
            },
            {}
          ) || {}
        )

        return {
          elementsGroupedByType: sectorElementsGroupedByType,
          id: sector.header.id,
          image: sector.header.image,
          name: sector.header.name,
        }
      })
    }
  },
  sectors: (state: RootState) => state.map.sectors,
  selectedElement: (state: RootState) => {
    if (rd.isFailure(state.map.mapData)) {
      return null
    } else if (rd.isSuccess(state.map.mapData)) {
      return state.map.mapData.value.elements?.find((element) => {
        return String(element.id) === String(state.map.selectedElementId)
      })
    }
  },
  selectedElementId: (state: RootState) => state.map.selectedElementId,
}

export const mapCurrentSectorSelector = createSelector(
  [mapSelectors.selectedElement, mapSelectors.sectors],
  (mapSelectedElement, mapSectors) => {
    return mapSectors?.find(
      (sector) => sector.header.id === mapSelectedElement?.sectorId
    )
  }
)

export const mapSelectedElementServices = createSelector(
  [mapCurrentSectorSelector, servicesSlice.selectors.data],
  (currentSector, serviceData) => {
    const filterServicesBySectorId = (services?: Service[]) => {
      if (!currentSector?.header.id || !services) return
      return (
        services.filter(
          (service) =>
            service.sectors.includes(currentSector?.header.id) &&
            !service.featured
        ) || []
      )
    }
    return {
      extraServices: filterServicesBySectorId(serviceData?.extraServices) || [],
      includedServices:
        filterServicesBySectorId(serviceData?.includedServices) || [],
      ticketServices:
        filterServicesBySectorId(serviceData?.ticketServices) || [],
    }
  }
)

export const mapSelectedElementHasVisibleSeatsSelector = createSelector(
  [mapSelectors.selectedElement],
  (mapSelectedElement) => {
    return !spotTypesWithoutSetupSeats.includes(
      mapSelectedElement?.subType as SpotType
    )
  }
)

export const mapCurrentSectorDetailsSelector = createSelector(
  [mapCurrentSectorSelector],
  (mapCurrentSector) => {
    return mapCurrentSector?.details.filter(
      (item) => item.type === SectorDetailType.ONLINE
    )
  }
)

export const mapSelectedElementSetupDataSelector = createSelector(
  [mapCurrentSectorSelector],
  (mapCurrentSector) => {
    const sectorDetails = mapCurrentSector?.details.filter(
      (item) => item.type === SectorDetailType.ONLINE
    )
    if (!sectorDetails) return
    const groupedData = sectorDetails.reduce<Record<string, MapSetupItem>>(
      (acc, item) => {
        const keyName = item.keyName

        acc[keyName as keyof object] = {
          ...acc[keyName],
          icon: mapSetupElements[item?.keyName as keyof object]['icon'],
          keyName: item?.keyName as MapSetupItemKeyName,
          label: t(mapSetupElements[item?.keyName as keyof object]['label'], {
            count: 1,
          }),
          [item['keyType']]: item.keyValue ?? 0,
          sectorId: item?.sectorId,
        }

        return acc
      },
      {}
    )
    const selectedElementSetupItems = Object.values(groupedData).filter(
      (item: MapSetupItem) =>
        item?.keyName !== MapSetupItemKeyName.COMBINED_LIMIT
    )

    const selectedElementSetupLimit =
      groupedData[MapSetupItemKeyName.COMBINED_LIMIT]
    return { selectedElementSetupItems, selectedElementSetupLimit }
  }
)
